import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilterGroup from 'src/components/molecules/filter-group';
import { Adjustments } from 'src/icons/adjustments';
import { X } from 'src/icons/x';
import { useSettingsStore } from 'src/store/settings-store';
import { Anchor } from 'src/types/anchor';
import { HandleChangeFilterValue } from 'src/types/handle-change-filter-value';
import { FilterGroup as IFilterGroup } from 'src/types/interfaces/filter-group';
import { Filters } from 'src/types/interfaces/filters';
import { toggleFilterItemSelection } from 'src/utils/filters/toggle-filter-item-selection';

interface Props {
  anchor: Anchor;
  filters: Filters;
  handleChangeFilterValue: HandleChangeFilterValue;
  resetFilters: () => void;
  filterGroups: IFilterGroup<string>[];
}

export const MoreFilters = ({
  anchor,
  filters,
  handleChangeFilterValue,
  resetFilters,
  filterGroups,
}: Props) => {
  const { t } = useTranslation(['spaces', 'common']);

  const settingsTheme = useSettingsStore(state => state.theme);

  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const handleCloseFilters = () => {
    setOpenFilters(false);
  };
  return (
    <>
      <Button
        color="primary"
        onClick={() => {
          setOpenFilters(true);
        }}
        startIcon={<Adjustments />}
        size="medium"
        variant={'outlined'}
        sx={{ minWidth: 160 }}>
        {t('common:filters.moreFilters')}
      </Button>

      <Drawer
        sx={{ height: '100%' }}
        anchor={anchor}
        open={openFilters}
        onClose={handleCloseFilters}>
        <Box
          sx={{
            width: anchor === 'top' || anchor === 'bottom' ? 'auto' : 380,
            overflowY: 'scroll',
          }}
          role="presentation">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              position: 'absolute',
              top: 0,
              width: '100%',
              backgroundColor: 'background.default',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderBottomColor:
                settingsTheme === 'light' ? '#e1e3ea' : '#2b2f3c',
              zIndex: 99999,
            }}>
            <Typography color="textPrimary" variant="subtitle1">
              {t('common:filters.moreFilters')}
            </Typography>
            <IconButton onClick={handleCloseFilters} size="small">
              <X fontSize="small" sx={{ color: 'text.primary' }} />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ minHeight: '100vh', pt: '61px', pb: '70px' }}>
            {filterGroups.map(group => (
              <Accordion
                key={group.type}
                disableGutters={true}
                sx={{ boxShadow: 'none' }}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1a-content"
                  id={group.id.toString()}>
                  {group.label}
                </AccordionSummary>

                {group.items.length > 0 ? (
                  <AccordionDetails>
                    <FilterGroup
                      items={group.items}
                      selected={(filters[group.type]?.value as string[]) ?? []}
                      refine={value => {
                        toggleFilterItemSelection(
                          group.type,
                          value,
                          group.singleOption,
                          filters,
                          handleChangeFilterValue,
                          group.filterOperator,
                        );
                      }}
                    />
                  </AccordionDetails>
                ) : null}

                {group.bottomSlot ? (
                  <AccordionActions sx={{ px: 2 }}>
                    {group.bottomSlot}
                  </AccordionActions>
                ) : null}
              </Accordion>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              position: 'absolute',
              bottom: 0,
              width: '100%',
              backgroundColor: 'background.default',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderTopColor: settingsTheme === 'light' ? '#e1e3ea' : '#2b2f3c',
            }}>
            <Button color="primary" onClick={resetFilters} variant="outlined">
              {t('common:filters.reset')}
            </Button>
            <Button
              color="primary"
              onClick={handleCloseFilters}
              variant="contained">
              {t('common:button.ok')}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
